"use client";

import * as React from "react";
import { ThemeProvider as NextThemesProvider } from "next-themes";
import { type ThemeProviderProps } from "next-themes";

export type ColorScheme = "sage" | "teal" | "lavender" | "amber" | "ocean" | "coral" | "mint" | "slate";

const SCHEME_KEY = "medaddwise-color-scheme";

export function ThemeProvider({ children, ...props }: ThemeProviderProps) {
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
    
    const stored = localStorage.getItem(SCHEME_KEY);
    const scheme = (stored && ["sage", "teal", "lavender", "amber", "ocean", "coral", "mint", "slate"].includes(stored)) 
      ? stored as ColorScheme 
      : "sage";
    
    document.documentElement.setAttribute("data-scheme", scheme);
  }, []);

  return (
    <NextThemesProvider
      {...props}
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
    >
      {children}
    </NextThemesProvider>
  );
}

export function useColorScheme() {
  const [scheme, setScheme] = React.useState<ColorScheme>("sage");
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
    const stored = localStorage.getItem(SCHEME_KEY) as ColorScheme | null;
    if (stored && ["sage", "teal", "lavender", "amber", "ocean", "coral", "mint", "slate"].includes(stored)) {
      setScheme(stored);
    } else {
      setScheme("sage");
    }
  }, []);

  const setColorScheme = React.useCallback((newScheme: ColorScheme) => {
    setScheme(newScheme);
    localStorage.setItem(SCHEME_KEY, newScheme);
    document.documentElement.setAttribute("data-scheme", newScheme);
  }, []);

  return { scheme, setColorScheme, mounted };
}
